/*
 *  -----------------------------------------------------------------------
 *    <copyright file="delete-signatory/index.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import { useMutation } from '@apollo/client/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DialogActions } from '@mui/material';
import React from 'react';

import Button from '@components/core/buttons/button';
import { CustomDialog, DialogContent, DialogTitle } from '@components/core/dialog';
import Loading from '@components/core/loading';
import { DeleteSignatoryProps } from '@components/reports/delete-signatory/delete-signatory.interface';
import { useTranslation } from '@hooks/index';

import { DELETE_REPORT_SIGNATORY, GET_REPORT_SIGNATORIES } from '@queries/reports';

const DeleteSignatory = ({
    open,
    onClose,
    studyId,
    template,
    signatoryId,
    currentSignatory,
    canEdit,
    onDelete,
    isLoading,
}: DeleteSignatoryProps) => {
    const { t } = useTranslation();
    const [deleteSignatory, { loading }] = useMutation(DELETE_REPORT_SIGNATORY);

    return (
        <CustomDialog
            onClose={onClose}
            aria-labelledby="delete-critical-variable"
            maxWidth="xl"
            scroll="paper"
            open={open}
            className="delete-cv-dialog"
        >
            <DialogTitle className="delete-cv-title">
                {t(currentSignatory?.inUse ? 'reports:signatoryInUse' : 'reports:deleteSignatory')}
                <FontAwesomeIcon
                    icon={['fal', 'times']}
                    className="fa-lg close-icon-cv"
                    onClick={() => onClose()}
                    data-testid="close-dialog"
                />
            </DialogTitle>

            <DialogContent className="dialog-content">
                {isLoading && <Loading float />}
                <span className="confirm-text !k-mb-5">
                    {t(currentSignatory?.inUse ? 'cm:unableToDeleteSignatoryText' : 'reports:confirmDeleteSignatory')}
                </span>
                {currentSignatory && (
                    <>
                        <p className="confirm-text !k-mb-2">
                            <span className="title">{t('cm:signatoryName')}: </span>
                            {currentSignatory?.firstName} {currentSignatory?.lastName}
                        </p>
                        <p className="confirm-text">
                            <span className="title">{t('reports:jobTitle')}: </span>
                            {currentSignatory?.jobTitle}
                        </p>
                    </>
                )}
            </DialogContent>

            <DialogActions className="delete-cv-actions">
                {(!currentSignatory?.inUse || !currentSignatory) && (
                    <>
                        <Button
                            className="delete-cv-btn"
                            id="cancel-delete-signatory"
                            text="cancel"
                            borderBtn
                            disabled={loading || isLoading}
                            onClick={() => {
                                onClose();
                            }}
                        />
                        <Button
                            className="delete-cv-btn"
                            id="delete-signatory"
                            text="delete"
                            disabled={loading || !canEdit || isLoading}
                            onClick={() => {
                                if (onDelete) {
                                    onDelete();
                                } else {
                                    deleteSignatory({
                                        variables: {
                                            studyId,
                                            template,
                                            id: signatoryId,
                                        },
                                        refetchQueries: [
                                            {
                                                query: GET_REPORT_SIGNATORIES,
                                                variables: {
                                                    studyId,
                                                    template,
                                                },
                                            },
                                        ],
                                    })
                                        .then(() => {
                                            onClose('success');
                                        })
                                        .catch(() => {
                                            onClose('error');
                                        });
                                }
                            }}
                        />
                    </>
                )}
            </DialogActions>
        </CustomDialog>
    );
};

export default DeleteSignatory;
